import { gql } from "@apollo/client";
import { AccountFragment } from "fragments/account";

// Two fragments, not one: a listing renders three columns and a section's
// `html` is unbounded. Pulling the body of every section to draw a table of
// names is the kind of thing that is invisible until a site has fifty of them.
//
// `frame` is here despite no listing rendering it, and the line it crosses is
// still the one above: it is a wrapper template, bounded by what it is for,
// not a body. It is here because the page editor's section PICKER copies it
// onto the element on select, and the alternative is a second query fired from
// inside an open modal — one whose answer arrives as a new identity, rebuilds
// the widget tree under the form and detaches whatever is being typed into
// (P139). Reading it with the list the editor already holds makes that select
// synchronous, and it costs one short string per section.
export const SectionListingFragment = gql`
  fragment SectionListingFragment on Section {
    id
    sectionId
    tags
    frame
    createdAt
    updatedAt
  }
`;

export const SectionFragment = gql`
  fragment SectionFragment on Section {
    ...SectionListingFragment

    html
    style
    script

    types
    values

    status
  }
  ${SectionListingFragment}
`;

// Every document reaches a section THROUGH its site — the same tenancy boundary
// pages go through, and the reason a section is a site's navbar rather than an
// organization's.
export const get = gql`
  query SectionGet($siteId: String!, $sectionId: String!) {
    viewer {
      id
      account {
        ...AccountFragment
        sites {
          id
          get(siteId: $siteId) {
            id
            sections {
              id
              get(sectionId: $sectionId) {
                ...SectionFragment
              }
            }
          }
        }
      }
    }
  }
  ${AccountFragment}
  ${SectionFragment}
`;

export const list = gql`
  query SectionList($siteId: String!, $input: ListInput) {
    viewer {
      id
      account {
        id
        sites {
          id
          get(siteId: $siteId) {
            id
            sections {
              id
              list(input: $input) {
                id
                last
                items {
                  ...SectionListingFragment
                }
              }
            }
          }
        }
      }
    }
  }
  ${SectionListingFragment}
`;

export const create = gql`
  mutation SectionNew($siteId: String!, $input: SectionInputCreate!) {
    viewer {
      account {
        sites {
          get(siteId: $siteId) {
            sections {
              create(input: $input) {
                ...SectionFragment
              }
            }
          }
        }
      }
    }
  }
  ${SectionFragment}
`;

export const edit = gql`
  mutation SectionEdit(
    $siteId: String!
    $sectionId: String!
    $input: SectionInputUpdate!
  ) {
    viewer {
      account {
        sites {
          get(siteId: $siteId) {
            sections {
              get(sectionId: $sectionId) {
                update(input: $input) {
                  ...SectionFragment
                }
              }
            }
          }
        }
      }
    }
  }
  ${SectionFragment}
`;

export const remove = gql`
  mutation SectionRemove($siteId: String!, $sectionId: String!) {
    viewer {
      account {
        sites {
          get(siteId: $siteId) {
            sections {
              get(sectionId: $sectionId) {
                remove
              }
            }
          }
        }
      }
    }
  }
`;

// The literal below must be a substring of what SectionQueries.id resolves to
// on the API side (api/code/src/schema/account/section). That id is now
// site-scoped — `${site.id}/sections/queries` — and this still matches it.
//
// Only `sections/queries`. The two page screens that read sections — the
// element picker and the preview — go through `sections.list` and
// `sections.get`, so what they cached is under THIS namespace and not the
// page's.
export const update = ({ sectionId }) => (cache) => {

  Object.keys(cache.data.data).forEach((key) => {
    if (key.includes(`sections/queries`)) {
      cache.data.delete(key);
    }
  });
};
